import { useQuery } from "@tanstack/react-query";
import { useAuth, useClerk, useUser } from "@clerk/react";
import { CheckCircle2, Settings2, ShieldCheck } from "lucide-react";
import { Redirect } from "wouter";
import { useSEO } from "@/hooks/use-seo";
import AdminDirectory from "./AdminDirectory";
import { PlatformContentManager } from "@/components/admin/PlatformContentManager";
import { EarlyAccessQueue } from "@/components/admin/EarlyAccessQueue";

interface AdminSession {
  access: "granted";
}

type AdminRequestError = Error & { status?: number };

async function fetchAdminSession(): Promise<AdminSession> {
  const response = await fetch("/api/admin/session", {
    credentials: "include",
  });

  if (!response.ok) {
    let message = "Unable to verify administrator access.";
    try {
      const body = await response.json();
      if (typeof body.error === "string") message = body.error;
    } catch {
      // Keep the generic message when the server does not return JSON.
    }

    const error = new Error(message) as AdminRequestError;
    error.status = response.status;
    throw error;
  }

  return response.json() as Promise<AdminSession>;
}

export default function AdminPage() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { openUserProfile, signOut } = useClerk();
  const { user } = useUser();

  useSEO({
    title: "Administrator Workspace | The Lifecycle Canvas",
    description: "Restricted administrator workspace.",
    noIndex: true,
  });

  const adminSession = useQuery<AdminSession, AdminRequestError>({
    queryKey: ["/api/admin/session", userId],
    queryFn: fetchAdminSession,
    enabled: isLoaded && Boolean(isSignedIn && userId),
  });

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (!isSignedIn) {
    return <Redirect to="/admin/sign-in" />;
  }

  if (adminSession.isPending) {
    return <LoadingScreen />;
  }

  if (adminSession.isError) {
    const denied = adminSession.error.status === 403;

    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-slate-200">
        <section className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center shadow-2xl">
          <h1 className="text-2xl font-semibold text-white">
            {denied ? "Access not authorized" : "Access could not be verified"}
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-400">
            {denied
              ? "The signed-in account does not have administrator access. Access is limited to the authorized administrator account."
              : "The server could not confirm administrator access. Please try again or sign in again."}
          </p>
          {denied && (
            <p className="mt-4 rounded-lg border border-slate-800 bg-slate-950/70 px-4 py-3 text-sm text-slate-300">
              Signed in as{" "}
              <span className="font-medium text-white">
                {user?.primaryEmailAddress?.emailAddress ??
                  user?.emailAddresses[0]?.emailAddress ??
                  "Email unavailable"}
              </span>
            </p>
          )}
          <button
            type="button"
            onClick={() => signOut({ redirectUrl: "/" })}
            className="mt-6 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800"
          >
            Sign out
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 px-6 py-10 text-slate-200">
      <div className="mx-auto max-w-[1440px]">
        <header className="flex flex-col gap-7 border-b border-slate-800 pb-7 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-800/70 bg-emerald-950/60 px-3 py-1.5 text-sm font-medium text-emerald-300">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              <span>Admin access confirmed</span>
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              Restricted workspace · account and content management
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-white">
              Platform administration
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Find accounts, review access, and manage shared Lifecycle Lab scenarios and toolbox content.
            </p>
          </div>

          <div className="flex w-full flex-col gap-4 rounded-xl border border-slate-800 bg-slate-900/70 p-4 lg:w-auto lg:min-w-[330px]">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-700 bg-slate-800 text-sm font-semibold text-teal-200">
                {user?.imageUrl ? (
                  <img
                    src={user.imageUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  (
                    user?.firstName?.[0] ??
                    user?.primaryEmailAddress?.emailAddress?.[0] ??
                    "A"
                  ).toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Signed in as
                </p>
                <p className="mt-1 truncate text-sm font-medium text-white">
                  {user?.fullName || user?.firstName || "Administrator"}
                </p>
                <p className="truncate text-xs text-slate-400">
                  {user?.primaryEmailAddress?.emailAddress ??
                    user?.emailAddresses[0]?.emailAddress ??
                    "Account email unavailable"}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-slate-800 pt-3">
              <button
                type="button"
                onClick={() => openUserProfile()}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
              >
                <Settings2 className="h-4 w-4" aria-hidden="true" />
                Account settings
              </button>
              <button
                type="button"
                onClick={() => signOut({ redirectUrl: "/" })}
                className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        <section className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
          <h2 className="flex items-center gap-2 text-lg font-medium text-white">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" aria-hidden="true" />
            Admin tools
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Manage accounts and shared platform content from this restricted workspace.
          </p>
        </section>

        <EarlyAccessQueue />
        <AdminDirectory />
        <PlatformContentManager />
      </div>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-slate-950 text-sm text-slate-400"
      aria-live="polite"
    >
      Verifying administrator access…
    </main>
  );
}
